import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { rendicionAnualService } from '../../../services/api';
import type { BalanceTotales, PlanCuenta, SaldoCuenta } from './types';
import BotonRefresh from './BotonRefresh';

interface Props {
  rendicionId: string;
  readonly: boolean;
  snapshotMode?: boolean;
  onBack: () => void;
  onNext: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

interface SaldoEditable {
  codigoCuenta: string;
  denominacion: string;
  debe: string;
  haber: string;
  readonly: boolean;
  nivel: number;
  rubroPadre: string | null;
}

function formatImporte(n: number | string): string {
  return Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseNum(s: string): number {
  if (!s) return 0;
  const cleaned = s.replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export default function Paso3Saldos({ rendicionId, readonly, snapshotMode, onBack, onNext, onDirtyChange }: Props) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const [editable, setEditable] = useState<Record<string, SaldoEditable>>({});
  // Acordeón: un único rubro raíz (nivel 1) abierto a la vez; sub-rubros dentro del raíz
  // se expanden individualmente. Cuando se abre otro raíz, se resetean los subs.
  const [rootAbierto, setRootAbierto] = useState<string | null>(null);
  const [subsAbiertos, setSubsAbiertos] = useState<Set<string>>(new Set());

  const { data: planData } = useQuery({
    queryKey: ['plan-cuentas'],
    queryFn: async () => {
      const res = await rendicionAnualService.getPlanCuentas();
      return res.data as PlanCuenta[];
    },
    staleTime: 60 * 60 * 1000,
  });

  const { data: saldosData, isLoading } = useQuery({
    queryKey: ['rendicion-saldos', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.listSaldos(rendicionId);
      return res.data as { items: SaldoCuenta[]; totales: BalanceTotales };
    },
    enabled: !snapshotMode,
    staleTime: snapshotMode ? Infinity : 0,
    refetchOnMount: snapshotMode ? false : 'always',
  });

  const plan = planData ?? [];
  const saldos = saldosData?.items ?? [];

  // Mapa de saldos del servidor por código — fuente de verdad para cuentas readonly (4.2.x)
  const saldosPorCodigo = useMemo(() => {
    const m = new Map<string, { debe: number; haber: number; readonly: boolean }>();
    for (const s of saldos) {
      m.set(s.codigoCuenta, {
        debe: Number(s.debe),
        haber: Number(s.haber),
        readonly: s.readonly,
      });
    }
    return m;
  }, [saldos]);

  // Hash que cambia cuando cambian valores del servidor (no solo cantidad)
  const saldosHash = useMemo(
    () =>
      saldos
        .map((s) => `${s.codigoCuenta}:${Number(s.debe)}:${Number(s.haber)}`)
        .sort()
        .join('|'),
    [saldos],
  );

  // Hidrata editables (solo para cuentas NO readonly). Los readonly se leen del servidor en render.
  useEffect(() => {
    if (!plan.length) return;
    const map: Record<string, SaldoEditable> = {};
    for (const p of plan) {
      const existente = saldosPorCodigo.get(p.codigo);
      const esReadonly = existente?.readonly ?? p.codigo.startsWith('4.2.0');
      map[p.codigo] = {
        codigoCuenta: p.codigo,
        denominacion: p.denominacion,
        debe: existente ? String(existente.debe) : '0',
        haber: existente ? String(existente.haber) : '0',
        readonly: esReadonly,
        nivel: p.nivel,
        rubroPadre: p.rubroPadre,
      };
    }
    setEditable(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.length, saldosHash]);

  // Mapa padre → hijos directos (para subtotales recursivos en rubros)
  const hijosPorPadre = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of plan) {
      if (!p.rubroPadre) continue;
      if (!m.has(p.rubroPadre)) m.set(p.rubroPadre, []);
      m.get(p.rubroPadre)!.push(p.codigo);
    }
    return m;
  }, [plan]);

  // Lookup por código (para recorrer ancestros en el acordeón)
  const planLookup = useMemo(() => {
    const m = new Map<string, PlanCuenta>();
    for (const p of plan) m.set(p.codigo, p);
    return m;
  }, [plan]);

  // Una cuenta es visible si todos sus ancestros expandibles están abiertos.
  function esVisible(codigo: string): boolean {
    const cuenta = planLookup.get(codigo);
    if (!cuenta) return false;
    if (cuenta.nivel === 1) return true; // raíces siempre visibles

    let padreCod = cuenta.rubroPadre;
    while (padreCod) {
      const padre = planLookup.get(padreCod);
      if (!padre) break;
      if (padre.nivel === 1) {
        // el raíz debe estar abierto
        if (rootAbierto !== padre.codigo) return false;
      } else if (hijosPorPadre.has(padre.codigo)) {
        // sub-rubro expandible
        if (!subsAbiertos.has(padre.codigo)) return false;
      }
      padreCod = padre.rubroPadre;
    }
    return true;
  }

  // Click en la fila de un rubro expandible.
  function toggleAcordeon(codigo: string) {
    const cuenta = planLookup.get(codigo);
    if (!cuenta) return;
    if (cuenta.nivel === 1) {
      // Raíz: solo uno abierto a la vez. Al abrir, expando todos sus subs.
      if (rootAbierto === codigo) {
        setRootAbierto(null);
        setSubsAbiertos(new Set());
      } else {
        const subs = new Set<string>();
        const recorrer = (c: string) => {
          const hijos = hijosPorPadre.get(c) ?? [];
          for (const h of hijos) {
            if (hijosPorPadre.has(h)) {
              subs.add(h);
              recorrer(h);
            }
          }
        };
        recorrer(codigo);
        setRootAbierto(codigo);
        setSubsAbiertos(subs);
      }
    } else {
      // Sub-rubro: toggle individual
      setSubsAbiertos((prev) => {
        const next = new Set(prev);
        if (next.has(codigo)) next.delete(codigo);
        else next.add(codigo);
        return next;
      });
    }
  }

  // Suma recursiva de descendientes (leaf = nivel >= 4 o sin hijos).
  function subtotalRubro(codigo: string): { debe: number; haber: number } {
    const hijos = hijosPorPadre.get(codigo);
    if (!hijos || hijos.length === 0) {
      // Leaf: usa el valor local o el del servidor si es readonly
      const e = editable[codigo];
      if (!e) return { debe: 0, haber: 0 };
      if (e.readonly) {
        const serv = saldosPorCodigo.get(codigo);
        return { debe: serv?.debe ?? 0, haber: serv?.haber ?? 0 };
      }
      return { debe: parseNum(e.debe), haber: parseNum(e.haber) };
    }
    let debe = 0;
    let haber = 0;
    for (const h of hijos) {
      const sub = subtotalRubro(h);
      debe += sub.debe;
      haber += sub.haber;
    }
    return { debe, haber };
  }

  const filtrado = useMemo(() => {
    const entries = Object.values(editable);
    const q = filter.trim().toLowerCase();
    if (q) {
      // Al buscar, ignoramos el acordeón para que el usuario vea todas las coincidencias
      return entries.filter(
        (e) => e.codigoCuenta.toLowerCase().includes(q) || e.denominacion.toLowerCase().includes(q),
      );
    }
    // Sin búsqueda → respeto el acordeón
    return entries.filter((e) => esVisible(e.codigoCuenta));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, filter, rootAbierto, subsAbiertos, planLookup, hijosPorPadre]);

  // Totales calculados localmente (para feedback instantáneo).
  // Las cuentas readonly (4.2.x) usan siempre el valor del servidor.
  const totalesLocales = useMemo(() => {
    let totalDebe = 0;
    let totalHaber = 0;
    let cuentas = 0;
    for (const e of Object.values(editable)) {
      let d: number;
      let h: number;
      if (e.readonly) {
        const serv = saldosPorCodigo.get(e.codigoCuenta);
        d = serv?.debe ?? 0;
        h = serv?.haber ?? 0;
      } else {
        d = parseNum(e.debe);
        h = parseNum(e.haber);
      }
      totalDebe += d;
      totalHaber += h;
      if (d > 0 || h > 0) cuentas++;
    }
    const diferencia = Math.round((totalDebe - totalHaber) * 100) / 100;
    return {
      totalDebe,
      totalHaber,
      diferencia,
      balanceado: Math.abs(diferencia) < 0.01,
      cuentasConMovimiento: cuentas,
    };
  }, [editable, saldosPorCodigo]);

  function updateSaldo(codigo: string, campo: 'debe' | 'haber', valor: string) {
    setEditable((prev) => ({
      ...prev,
      [codigo]: { ...prev[codigo], [campo]: valor },
    }));
  }

  // Dirty-check: hay cambios no guardados si algún saldo editable (no readonly)
  // difiere del último valor que vino del servidor.
  const isDirty = useMemo(() => {
    for (const e of Object.values(editable)) {
      if (e.readonly) continue;
      const serv = saldosPorCodigo.get(e.codigoCuenta);
      const servDebe = serv?.debe ?? 0;
      const servHaber = serv?.haber ?? 0;
      const localDebe = parseNum(e.debe);
      const localHaber = parseNum(e.haber);
      if (Math.abs(localDebe - servDebe) > 0.001 || Math.abs(localHaber - servHaber) > 0.001) {
        return true;
      }
    }
    return false;
  }, [editable, saldosPorCodigo]);

  // Informo al shell del estado dirty (para que el stepper pregunte antes de cambiar)
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Solo enviamos cuentas con movimiento o las auto-calculadas (4.2.x)
      const saldos = Object.values(editable)
        .filter((e) => parseNum(e.debe) > 0 || parseNum(e.haber) > 0 || e.readonly)
        .map((e) => ({
          codigoCuenta: e.codigoCuenta,
          debe: parseNum(e.debe),
          haber: parseNum(e.haber),
        }));
      return rendicionAnualService.saveSaldos(rendicionId, saldos);
    },
    onSuccess: () => {
      toast.success('Saldos guardados');
      queryClient.invalidateQueries({ queryKey: ['rendicion-saldos', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-validaciones', rendicionId] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo guardar'),
  });

  if (isLoading) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">Cargando plan de cuentas...</div>;
  }

  function refrescarPaso() {
    queryClient.invalidateQueries({ queryKey: ['rendicion-saldos', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['plan-cuentas'] });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end -mb-2">
        <BotonRefresh onClick={refrescarPaso} title="Actualizar paso" />
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          Carga de Saldos por Cuenta
        </h2>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-900 mb-4">
          Cargue los saldos <strong>Debe</strong> y <strong>Haber</strong> de cada cuenta usada. Las cuentas{' '}
          <strong>4.2.01 a 4.2.04</strong> se auto-calculan desde los aportes del Paso 2 (solo lectura).
          Los reportes del Paso 5 se generan automáticamente a partir de estos saldos.
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <TotalBox label="Total Debe" value={totalesLocales.totalDebe} />
          <TotalBox label="Total Haber" value={totalesLocales.totalHaber} />
          <TotalBox
            label="Diferencia"
            value={Math.abs(totalesLocales.diferencia)}
            tone={totalesLocales.balanceado ? 'success' : 'danger'}
          />
          <TotalBox label="Cuentas con movimiento" value={totalesLocales.cuentasConMovimiento} noCurrency />
        </div>

        <div className="mb-3">
          <input
            type="text"
            placeholder="Buscar por código o denominación..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="overflow-x-auto max-h-[600px] overflow-y-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Código</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Denominación</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">Debe</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">Haber</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filtrado.map((e) => {
                const tieneHijos = hijosPorPadre.has(e.codigoCuenta);
                const esRubro = tieneHijos; // todo rubro expandible se trata como agregado
                const esRaiz = e.nivel === 1;
                const abierto = esRaiz
                  ? rootAbierto === e.codigoCuenta
                  : subsAbiertos.has(e.codigoCuenta);
                const rowClass = esRaiz
                  ? 'bg-blue-50 font-semibold text-blue-900'
                  : e.nivel === 2
                  ? 'bg-blue-50/60 font-semibold text-blue-900'
                  : esRubro
                  ? 'bg-gray-50 text-gray-800 font-medium'
                  : '';
                const rowClick = tieneHijos ? 'cursor-pointer hover:bg-opacity-80' : '';
                // Para cuentas readonly (4.2.x) el valor auténtico está en el servidor
                const servidor = saldosPorCodigo.get(e.codigoCuenta);
                // Rubros (con hijos): muestran subtotal recursivo. Hojas: su valor.
                const subtotales = esRubro ? subtotalRubro(e.codigoCuenta) : null;
                const debeDisplay = esRubro
                  ? String(subtotales!.debe)
                  : e.readonly
                  ? String(servidor?.debe ?? 0)
                  : e.debe;
                const haberDisplay = esRubro
                  ? String(subtotales!.haber)
                  : e.readonly
                  ? String(servidor?.haber ?? 0)
                  : e.haber;
                return (
                  <tr
                    key={e.codigoCuenta}
                    className={`${rowClass} ${rowClick}`}
                    onClick={tieneHijos ? () => toggleAcordeon(e.codigoCuenta) : undefined}
                  >
                    <td className="px-3 py-1.5 font-mono text-xs">
                      <span className="inline-flex items-center gap-1">
                        {tieneHijos ? (
                          <span className="w-4 text-center text-gray-500 select-none">
                            {abierto ? '▼' : '▶'}
                          </span>
                        ) : (
                          <span className="w-4" />
                        )}
                        {e.codigoCuenta}
                      </span>
                    </td>
                    <td
                      className="px-3 py-1.5"
                      style={{ paddingLeft: `${12 + (e.nivel - 1) * 12}px` }}
                    >
                      {e.denominacion}
                      {e.readonly && (
                        <span className="ml-2 text-xs text-gray-500">(auto desde aportes)</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {esRubro ? (
                        <span className="font-mono">{formatImporte(parseNum(debeDisplay))}</span>
                      ) : (
                        <input
                          type="text"
                          inputMode="decimal"
                          readOnly={readonly || e.readonly}
                          value={debeDisplay}
                          onChange={(ev) => updateSaldo(e.codigoCuenta, 'debe', ev.target.value)}
                          className={`w-28 border border-gray-300 rounded px-2 py-1 text-right text-xs font-mono ${
                            e.readonly || readonly ? 'bg-gray-100 text-gray-500' : ''
                          }`}
                        />
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {esRubro ? (
                        <span className="font-mono">{formatImporte(parseNum(haberDisplay))}</span>
                      ) : (
                        <input
                          type="text"
                          inputMode="decimal"
                          readOnly={readonly || e.readonly}
                          value={haberDisplay}
                          onChange={(ev) => updateSaldo(e.codigoCuenta, 'haber', ev.target.value)}
                          className={`w-28 border border-gray-300 rounded px-2 py-1 text-right text-xs font-mono ${
                            e.readonly || readonly ? 'bg-gray-100 text-gray-500' : ''
                          }`}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtrado.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-500">
                    Sin coincidencias.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Estado del balance — debajo de la tabla, sin botón guardar (se unificó con "Continuar") */}
        <div className="mt-4 border-t border-gray-200 pt-4 flex items-center gap-2 flex-wrap">
          {totalesLocales.balanceado ? (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-green-100 text-green-800">
              ✓ BALANCEADO
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-red-100 text-red-800">
              ✗ DESBALANCEADO · diferencia $ {formatImporte(Math.abs(totalesLocales.diferencia))}
            </span>
          )}
          {isDirty && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
              ● Cambios sin guardar
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
        >
          ← Anterior
        </button>
        <button
          type="button"
          disabled={saveMutation.isPending}
          onClick={async () => {
            if (isDirty && !readonly) {
              try {
                await saveMutation.mutateAsync();
              } catch {
                return; // el toast de error ya lo muestra el onError
              }
            }
            onNext();
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50"
        >
          {saveMutation.isPending
            ? 'Guardando...'
            : isDirty && !readonly
            ? 'Guardar y continuar →'
            : 'Continuar →'}
        </button>
      </div>
    </div>
  );
}

function TotalBox({
  label,
  value,
  tone = 'default',
  noCurrency,
}: {
  label: string;
  value: number;
  tone?: 'default' | 'success' | 'danger';
  noCurrency?: boolean;
}) {
  const toneClass =
    tone === 'success' ? 'text-green-700' : tone === 'danger' ? 'text-red-700' : 'text-gray-900';
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
      <div className="text-xs font-semibold uppercase text-gray-600 mb-1">{label}</div>
      <div className={`text-lg font-bold font-mono ${toneClass}`}>
        {noCurrency ? value : `$ ${formatImporte(value)}`}
      </div>
    </div>
  );
}
